<script setup lang="ts">
import { computed, h } from 'vue'
import type { SummaryMethodProps } from '@/types/shared/element-plus'
import type { TableColumnCtx } from 'element-plus'
import { isFinite } from 'lodash'

import { currencyFormatter, megawattFormatter, uptimeFormatter } from '@/helpers/ep/formatters'
import { getAvg, getSum } from '@/util/calc'
import { formatCurrency, formatMegawatts, formatPercentage } from '@/util/formatters'
import { formatHourEnding, formatTimeWindow, getHourEndingDisplayString } from '@/helpers/eos/table-formatters'
import type { PerformanceTrackLine } from '@/types/eos/PerformanceTrackerCommon'
import type { DAPerformanceTrackLine } from '@/types/eos/DARTPerformanceTracker'
import type { ORPerformanceTrackLine } from '@/types/eos/ORPerformanceTracker'

export interface GenericMonthlySummary {
  [key: string]: any
}

const props = defineProps<
  // Common props
  {
    timezone?: string
    monthlySummary?: GenericMonthlySummary
    showMonthlySummary?: boolean
  } &
    // Discriminated union over mode and data
    (
      | {
          mode: 'DA'
          data: DAPerformanceTrackLine[]
        }
      | {
          mode: 'OR'
          data: ORPerformanceTrackLine[]
        }
    )
>()

// COLUMN & SUMMARY CONFIGURATION

interface ColumnDef {
  prop: string
  label: string
  unit: string
  align?: 'left' | 'center' | 'right'
  minWidth?: number
  formatter?: (row: any, column: TableColumnCtx<any>, cellValue: any) => string
  aggregator?: (nums: number[]) => number
  summaryFormatter?: (val: number) => string
  monthlyKey?: string
}

const activeColumns = computed<ColumnDef[]>(() => {
  const baseColumns: ColumnDef[] = [
    { prop: 'hour_utc', label: 'Hour Ending', unit: 'EST', align: 'center', minWidth: 115 },
    {
      prop: 'avg_flow_kw',
      label: 'Avg Flow',
      unit: 'KWh',
      align: 'center',
      minWidth: 130,
      formatter: megawattFormatter,
      aggregator: getSum,
      summaryFormatter: formatMegawatts,
      monthlyKey: 'flow_sum',
    }, // OR uses flow_kw_sum, we can handle this mapping in the summary
    {
      prop: 'raw_baseline_kw',
      label: 'Raw Baseline',
      unit: 'KWh',
      align: 'center',
      minWidth: 130,
      formatter: megawattFormatter,
      aggregator: getSum,
      summaryFormatter: formatMegawatts,
      monthlyKey: 'raw_baseline_kw_sum',
    },
    {
      prop: 'non_curtailable_load_kw',
      label: 'Non-Curtailable Load',
      unit: 'KWh',
      align: 'right',
      minWidth: 140,
      formatter: megawattFormatter,
      aggregator: getSum,
      summaryFormatter: formatMegawatts,
      monthlyKey: 'non_curtailable_load_kw_sum',
    },
  ]

  const daSpecific: ColumnDef[] = [
    {
      prop: 'in_day_adjustment_kw',
      label: 'In-Day Adjustment',
      unit: 'KWh',
      align: 'right',
      minWidth: 130,
      formatter: megawattFormatter,
      aggregator: getSum,
      summaryFormatter: formatMegawatts,
      monthlyKey: 'in_day_adjustment_kw_sum',
    },
    {
      prop: 'hourly_commitment_kw',
      label: 'Hourly Commit.',
      unit: 'KWh',
      align: 'right',
      minWidth: 130,
      formatter: megawattFormatter,
      aggregator: getSum,
      summaryFormatter: formatMegawatts,
      monthlyKey: 'hourly_commitment_kw_sum',
    },
    {
      prop: 'hourly_performance_kw',
      label: 'Hourly Perform.',
      unit: 'KWh',
      align: 'right',
      minWidth: 130,
      formatter: megawattFormatter,
      aggregator: getSum,
      summaryFormatter: formatMegawatts,
      monthlyKey: 'hourly_performance_kw_sum',
    },
    {
      prop: 'da_lmp',
      label: 'DA LMP',
      unit: '$/MWh',
      align: 'right',
      minWidth: 130,
      formatter: currencyFormatter,
      aggregator: getAvg,
      summaryFormatter: formatCurrency,
      monthlyKey: 'da_lmp_average',
    },
    {
      prop: 'rt_lmp',
      label: 'RT LMP',
      unit: '$/MWh',
      align: 'right',
      minWidth: 130,
      formatter: currencyFormatter,
      aggregator: getAvg,
      summaryFormatter: formatCurrency,
      monthlyKey: 'rt_lmp_average',
    },
    {
      prop: 'pr_strike_price',
      label: 'Strike Price',
      unit: '$/MWh',
      align: 'right',
      minWidth: 130,
      formatter: currencyFormatter,
      aggregator: getAvg,
      summaryFormatter: formatCurrency,
      monthlyKey: 'pr_strike_price_average',
    },
    {
      prop: 'da_earnings',
      label: 'DA Earnings',
      unit: '$',
      align: 'right',
      minWidth: 130,
      formatter: currencyFormatter,
      aggregator: getSum,
      summaryFormatter: formatCurrency,
      monthlyKey: 'da_earnings_sum',
    },
    {
      prop: 'rt_earnings',
      label: 'RT Earnings',
      unit: '$',
      align: 'right',
      minWidth: 130,
      formatter: currencyFormatter,
      aggregator: getSum,
      summaryFormatter: formatCurrency,
      monthlyKey: 'rt_earnings_sum',
    },
    {
      prop: 'mwh_earnings',
      label: 'MWh Earnings',
      unit: '$',
      align: 'right',
      minWidth: 130,
      formatter: currencyFormatter,
      aggregator: getAvg,
      summaryFormatter: formatCurrency,
      monthlyKey: 'mwh_earnings_average',
    },
  ]

  const orSpecific: ColumnDef[] = [
    {
      prop: 'availability_kwh',
      label: 'Availability',
      unit: 'KWh',
      align: 'right',
      minWidth: 130,
      formatter: megawattFormatter,
      aggregator: getSum,
      summaryFormatter: formatMegawatts,
      monthlyKey: 'availability_kwh_sum',
    },
    {
      prop: 'rt_gen_spin_mcp',
      label: 'MCP',
      unit: '$/MWh',
      align: 'right',
      minWidth: 130,
      formatter: currencyFormatter,
      aggregator: getAvg,
      summaryFormatter: formatCurrency,
      monthlyKey: 'rt_gen_spin_mcp_average',
    },
    {
      prop: 'performance_factor',
      label: 'Perform. Factor',
      unit: 'Ratio',
      align: 'right',
      minWidth: 130,
      formatter: (_row, _column, cellValue) => (isFinite(cellValue) ? cellValue.toFixed(2) : '-'),
      aggregator: getAvg,
      summaryFormatter: (val) => (isFinite(val) ? val.toFixed(2) : '-'),
      monthlyKey: 'performance_factor',
    },
  ]

  const sharedTail: ColumnDef[] = [
    {
      prop: 'customer_earnings',
      label: 'Customer Earnings',
      unit: '$',
      align: 'right',
      minWidth: 130,
      formatter: currencyFormatter,
      aggregator: getSum,
      summaryFormatter: formatCurrency,
      monthlyKey: 'customer_earnings_sum',
    },
    {
      prop: 'site_split_percentage',
      label: 'Site Split',
      unit: '%',
      align: 'right',
      minWidth: 130,
      formatter: uptimeFormatter,
      aggregator: getAvg,
      summaryFormatter: formatPercentage,
      monthlyKey: 'site_split_percentage_average',
    },
  ]

  if (props.mode === 'DA') return [...baseColumns, ...daSpecific, ...sharedTail]
  if (props.mode === 'OR') return [...baseColumns, ...orSpecific, ...sharedTail]
  return [...baseColumns, ...sharedTail]
})

// TABLE BEHAVIOR LOGIC

interface SpanMethodProps {
  row: PerformanceTrackLine
  column: TableColumnCtx<PerformanceTrackLine>
  rowIndex: number
  columnIndex: number
}

function colspanManager({ columnIndex, row }: SpanMethodProps) {
  // If no dispatch, span the second column (first data column) across the rest of the table
  if (props.mode === 'OR') return [1, 1] // OR doesn't have the same colspan logic as DA, row data is still relevant even without dispatch
  if (!row.affecting_dispatch && columnIndex === 1) return [1, activeColumns.value.length - 1]
  if (!row.affecting_dispatch && columnIndex > 1) return [0, 0]
  return [1, 1]
}

/**
 * Collects a column's data _if_ it fulfills mode criteria:
 * - DA or OR: only rows flagged participating count
 * @param data the table data, probably 24 rows for 24 hs
 * @returns the filtered array - i.e. could have any amount of values from 0 to 24 depending on how many dispatches/participating rows there are
 */
const getColumnData = (data: PerformanceTrackLine[], prop: keyof PerformanceTrackLine): number[] => {
  return data.reduce((acc, row) => {
    const value = row[prop]

    const isDAEligible = props.mode === 'DA' && (row as DAPerformanceTrackLine).participating_in_program
    const isOREligible = props.mode === 'OR' && (row as ORPerformanceTrackLine).participating_in_program

    const shouldCollect = isFinite(value) && (isDAEligible || isOREligible)

    if (shouldCollect) acc.push(value as number)
    return acc
  }, [] as number[])
}

const getTableSummary = (param: SummaryMethodProps<PerformanceTrackLine>) => {
  const { columns, data } = param
  const summaries: string[] = []

  // We're building _either one or two_ rows:
  // - data-based daily summary
  // - query-based monthly summary (if showMonthlySummary is true)
  // but we can return a single row, so we split lines with `/n`

  columns.forEach((column, index) => {
    if (index === 0) {
      let label = 'Daily'
      if (props.showMonthlySummary) label += props.monthlySummary ? '\nMonthly' : '\nLoading...'
      summaries[index] = label
      return
    }

    // We check config to see how to aggregate
    const colConfig = activeColumns.value.find((c) => c.prop === column.property)
    if (colConfig && colConfig.aggregator && colConfig.summaryFormatter) {
      // We extract the relevant values for this column based on mode criteria (dispatch for DA, participation for OR)
      const dailyValues = getColumnData(data, colConfig.prop as keyof PerformanceTrackLine)

      // If we have values, we aggregate and format, else we put a dash.
      let cellValue = dailyValues.length > 0 ? colConfig.summaryFormatter(colConfig.aggregator(dailyValues)) : '-'

      // If showMonthlySummary is true, we try to get the monthly summary value from props and append it with a line break
      // (since we have a single summary row)
      if (props.showMonthlySummary) {
        if (props.monthlySummary && colConfig.monthlyKey) {
          // Fallback for key differences like flow_sum vs flow_kw_sum (called differently in OR vs DA)
          const mKey = colConfig.prop === 'avg_flow_kw' && props.mode === 'OR' ? 'flow_kw_sum' : colConfig.monthlyKey

          // We access the monthly summary value, which is passed as prop
          const mVal = props.monthlySummary[mKey]

          // Format
          cellValue += mVal !== undefined ? `\n${colConfig.summaryFormatter(mVal as number)}` : '\n-'
        } else {
          cellValue += '\n-'
        }
      }
      summaries[index] = cellValue
    } else {
      summaries[index] = props.showMonthlySummary ? '-\n-' : '-'
    }
  })

  return summaries
}

// CUSTOM FORMATTERS

const hourEndingFormatter = (row: PerformanceTrackLine) => formatHourEnding(row.hour_utc, props.timezone)

function hourlyPerformanceFormatter(...params: Parameters<typeof megawattFormatter>) {
  const [row] = params as [PerformanceTrackLine, ...unknown[]]
  const stringValue = megawattFormatter(...params)

  // DA logic
  if (props.mode === 'DA') {
    const dartRow = row as DAPerformanceTrackLine
    // If performance is less than commitment, color red
    return h(
      'span',
      { class: dartRow.hourly_commitment_kw > dartRow.hourly_performance_kw ? 'text-orange-600' : 'text-emerald-500' },
      stringValue
    )
  }
  // OR logic
  if (props.mode === 'OR') {
    const orRow = row as ORPerformanceTrackLine
    return h('span', { class: orRow.performance_factor < 1 ? 'text-orange-600' : 'text-emerald-500' }, stringValue)
  }
  return h('span', { class: 'text-emerald-500' }, stringValue)
}
</script>

<template>
  <el-table :data="data" size="small" :span-method="colspanManager" :summary-method="getTableSummary" show-summary>
    <template v-for="(col, index) in activeColumns" :key="col.prop">
      <!-- First column is index, we always display it -->
      <el-table-column v-if="index === 0" v-bind="col" fixed="left" :formatter="hourEndingFormatter">
        <template #header>
          <span>{{ col.label }}</span
          ><br /><span>({{ col.unit }})</span>
        </template>
        <template #default="{ row }">
          <div class="hour-ending-cell">
            <el-tooltip>
              <template #content> {{ getHourEndingDisplayString(row.hour_utc, props.timezone) }}</template>
              <template #default>
                <span class="cursor-help">{{ formatHourEnding(row.hour_utc, props.timezone) }}</span>
              </template>
            </el-tooltip>

            <!-- MISO badge with tooltip -->
            <el-tooltip v-if="row.affecting_dispatch" effect="dark">
              <template #content>
                <div>
                  <strong>ID:</strong> {{ row.affecting_dispatch.dispatch_id }}<br />
                  <strong>Market:</strong> {{ row.affecting_dispatch.program_market }}<br />
                  <strong>Program:</strong> {{ row.affecting_dispatch.program_name }}<br />
                  <strong>Time Window:</strong>
                  {{
                    formatTimeWindow(
                      row.affecting_dispatch.start_time_utc,
                      row.affecting_dispatch.end_time_utc,
                      timezone
                    )
                  }}
                  <template v-if="row.affecting_dispatch.overlapped_by_dispatch_id">
                    <br /><strong>Overlapped by Dispatch ID:</strong>
                    {{ row.affecting_dispatch.overlapped_by_dispatch_id }}
                  </template>
                </div>
              </template>
              <template #default>
                <span class="voltus-dispatch-badge">
                  <img src="/assets/pictures/misoLogo.png" alt="MISO" class="voltus-miso-logo" />
                </span>
              </template>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>

      <!-- First column is the one that stretches to the end of the table in case it's no-dispatch -->
      <!-- Else it's avg flow -->
      <el-table-column v-else-if="col.prop === 'avg_flow_kw'" v-bind="col">
        <template #header>
          <span>{{ col.label }}</span
          ><br /><span>({{ col.unit }})</span>
        </template>
        <template #default="{ row, column }">
          <div v-if="mode === 'DA' && !row.affecting_dispatch" class="no-dispatch-cell">No DA PR dispatch</div>
          <span v-else>
            {{ megawattFormatter(row, column, row.avg_flow_kw) }}
          </span>
        </template>
      </el-table-column>

      <!-- Key metric gets passed an custom formatter -->
      <el-table-column
        v-else-if="col.prop === 'hourly_performance_kw' || col.prop === 'performance_factor'"
        v-bind="col"
        :formatter="hourlyPerformanceFormatter"
      >
        <template #header>
          <span>{{ col.label }}</span
          ><br /><span>({{ col.unit }})</span>
        </template>
      </el-table-column>

      <!-- The rest is config-based -->
      <el-table-column v-else v-bind="col">
        <template #header>
          <span>{{ col.label }}</span
          ><br /><span>({{ col.unit }})</span>
        </template>
      </el-table-column>
    </template>
  </el-table>
</template>

<style scoped lang="scss">
.hour-ending-cell {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
}

.voltus-dispatch-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border: 1px solid var(--component-stroke-primary);
  border-radius: 9999px;
  background-color: #fff;
}

.voltus-miso-logo {
  height: 12px;
  width: auto;
  display: block;
}

.no-dispatch-cell {
  width: 100%;
  color: var(--text-secondary);
  text-align: center;
  pointer-events: none;
}

:deep() {
  .el-table__cell {
    padding: 12px;
  }

  th.el-table__cell {
    padding: 4px 0;
    color: var(--text-tertiary);
    text-align: left;
  }
}

:deep(.el-table__footer-wrapper) {
  tfoot {
    td.el-table__cell {
      background-color: var(--fill-secondary);

      .cell {
        font-weight: 600;
        white-space: pre-line;
      }
    }
  }
}
</style>
